import React from 'react';
import { Desc, T, t } from '@sm/intl';
import { Box } from '@wds/box';
import { createURL } from '@sm/utils';
import { Grid } from '@wds/grid';
import { generateMetricsAttribute } from '@sm/metrics';
import { IconLogoGoldie } from '@wds/icons';
import { Typography } from '@wds/typography';
import { Button } from '@wds/button';

import { ShadedBackgroundColorProp } from '~app/components/EndpageImage';

import useStyles from './useStyles';

type PollingBannerProps = {
  utSource: string;
  utSource2: string;
  utSource3: string;
  amplitudeEventName: string;
  templateType: string;
  surveyId: string;
  collectorId: string;
  respondentId: string;
  shadedBackgroundColor: ShadedBackgroundColorProp;
  textBannerSellPoint1: Desc;
  textBannerSellPoint2: Desc;
  textBannerSellPoint3: Desc;
  textBannerCTA: Desc;
  textBannerCTARoute: string;
  textBannerCtaUsage: string;
};

export default function PollingBanner({
  utSource,
  utSource2,
  utSource3,
  amplitudeEventName,
  templateType,
  surveyId,
  collectorId,
  respondentId,
  textBannerSellPoint1,
  textBannerSellPoint2,
  textBannerSellPoint3,
  shadedBackgroundColor,
  textBannerCTA,
  textBannerCTARoute,
  textBannerCtaUsage,
}: PollingBannerProps): React.ReactElement {
  const { bannerWrapper, bannerContainer, bannerLogoGoldie, bannerText } = useStyles({ shadedBackgroundColor });

  return (
    /* PollingBanner */
    <Box className={bannerWrapper} display="flex" flexAlign="center" flexDirection="column" px={5} pt={6} pb={3}>
      <Box className={bannerContainer}>
        <Grid mb={5} noGutters>
          <Grid.Item lg={2} md={1} sm={12} xs={12}>
            <Box display="flex" flexDirection="column" pb={3}>
              <Box className={bannerLogoGoldie}>
                <IconLogoGoldie title="goldie" color="light" />
              </Box>
            </Box>
          </Grid.Item>
          <Grid.Item md={2} sm={12} xs={12}>
            <Box pt={1} pl={3} pr={3} className={bannerText} display="flex" flexDirection="column">
              <Typography variant="bodySm" color="light">
                <T desc={textBannerSellPoint1} html />
              </Typography>
            </Box>
          </Grid.Item>
          <Grid.Item md={2} sm={12} xs={12}>
            <Box p={1} className={bannerText} display="flex" flexDirection="column">
              <Typography variant="bodySm" color="light">
                <T desc={textBannerSellPoint2} html />
              </Typography>
            </Box>
          </Grid.Item>
          <Grid.Item lg={2} md={3} sm={12} xs={12}>
            <Box pl={1} pb={1} pt={1} className={bannerText} display="flex" flexDirection="column">
              <Typography variant="bodySm" color="light">
                <T desc={textBannerSellPoint3} html />
              </Typography>
            </Box>
          </Grid.Item>
          <Grid.Item md={4} sm={12} xs={12}>
            <Box className={bannerWrapper} display="flex" flexAlign="center" flexDirection="column" mt={3}>
              <Button
                color="alt"
                size="sm"
                href={createURL(textBannerCTARoute, {
                  ut_source: utSource,
                  ut_source2: utSource2,
                  ut_source3: utSource3,
                  ut_ctatext: textBannerCTA?.defaultMessage ?? '',
                })}
                data-sm-metrics={generateMetricsAttribute({
                  data: {
                    amplitudeEvent: amplitudeEventName,
                    itemSelected: textBannerCtaUsage,
                    templateType,
                    surveyId,
                    collectorId,
                    respondentId,
                    log: {
                      message: `${utSource}-${utSource2}-${utSource3}-${textBannerCTA.defaultMessage}`,
                    },
                  },
                })}
              >
                {t(textBannerCTA)}
              </Button>
            </Box>
          </Grid.Item>
        </Grid>
      </Box>
    </Box>
  );
}
